import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useSession } from '../session/useSession'
import { useI18n } from '../i18n/LocaleContext'
import { Button } from '../ui/Button'
import { CheckIcon } from '../ui/icons'
import { Logo } from '../ui/Logo'

// The webhook usually lands within a few seconds of the redirect. Poll the
// profile/subscription query for a short while so a signed-in re-subscriber
// walks straight back in instead of staring at a stale paywall.
const PROVISION_POLL_MS = 3_000
const PROVISION_POLL_LIMIT = 10

/**
 * Stripe's success_url lands here.
 *
 * The webhook provisions the workspace out of band, so at this moment the user
 * usually has no session at all — the flow deliberately hands them to /login,
 * where the emailed magic link or Google sign-in takes over. The one exception
 * is a user who was already signed in (re-subscribing from the paywall): for
 * them we refresh the token so the new organization_id claim lands, re-read
 * the subscription status until the webhook has written it, and send them
 * straight into the app.
 */
export function CheckoutSuccess() {
  const session = useSession()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useI18n()

  useEffect(() => {
    // Harmless when signed out; the promise just rejects and is swallowed.
    supabase.auth.refreshSession().catch(() => {})
  }, [])

  const waitingForWebhook =
    session.status === 'unprovisioned' || session.status === 'no_subscription'

  useEffect(() => {
    if (!waitingForWebhook) return
    let attempts = 0
    const timer = window.setInterval(() => {
      attempts += 1
      if (attempts > PROVISION_POLL_LIMIT) {
        window.clearInterval(timer)
        return
      }
      void queryClient.invalidateQueries({ queryKey: ['profile'] })
      void queryClient.invalidateQueries({ queryKey: ['subscription'] })
    }, PROVISION_POLL_MS)
    return () => window.clearInterval(timer)
  }, [waitingForWebhook, queryClient])

  useEffect(() => {
    if (session.status === 'authenticated') navigate('/', { replace: true })
  }, [session.status, navigate])

  return (
    <CheckoutShell>
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
        <CheckIcon size={24} />
      </span>
      <div>
        <h1 className="font-display text-[24px] font-normal text-gray-900">{t.checkout.successTitle}</h1>
        <p className="mt-2.5 text-[13.5px] leading-relaxed text-gray-600">
          {t.checkout.successBody}
        </p>
        <p className="mt-2 text-[12px] text-gray-400">{t.checkout.successHint}</p>
      </div>
      <Link to="/login" className="block">
        <Button size="lg" className="w-full">
          {t.checkout.goToSignIn}
        </Button>
      </Link>
    </CheckoutShell>
  )
}

export function CheckoutCancel() {
  const { t } = useI18n()
  return (
    <CheckoutShell>
      <div>
        <h1 className="font-display text-[24px] font-normal text-gray-900">{t.checkout.cancelTitle}</h1>
        <p className="mt-2.5 text-[13.5px] leading-relaxed text-gray-600">
          {t.checkout.cancelBody}
        </p>
      </div>
      <Link to="/pricing" className="block">
        <Button variant="secondary" size="lg" className="w-full">
          {t.checkout.backToPricing}
        </Button>
      </Link>
    </CheckoutShell>
  )
}

function CheckoutShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-canvas p-6">
      <div className="mb-8">
        <Logo />
      </div>
      <div className="w-full max-w-sm space-y-6 rounded-xl bg-surface p-8 text-center shadow-card">
        {children}
      </div>
    </main>
  )
}
